import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Loader2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ConfirmButton } from "@/components/admin/ConfirmButton";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { OfferCard, useOfferMoney } from "@/components/offers/OfferCard";
import { GroupMembersField, uniqueMembers } from "@/components/offers/GroupMembersField";
import { Switch } from "@/components/ui/switch";
import { OfferProfit } from "@/components/admin/Profit";
import { BusinessRefsField, splitPool } from "@/components/admin/MinisiteScope";

const PRESETS = [1, 3, 6, 12, 24, 36, 60];
const num = (v) => Number(String(v).replace(",", "."));
const SEL = "mt-1 w-full min-w-0 rounded-md border border-neutral-700 bg-black/50 px-2 py-2 text-sm text-white";

const Field = ({ label, children }) => <label className="min-w-0 text-xs text-neutral-400"><span className="break-words">{label}</span>{children}</label>;

const initialTerms = (offer, initial) => {
  const tm = offer?.terms || {};
  return { months: tm.months ?? 12, mode: tm.mode || "recurring", interval: tm.interval ?? 1, price_ht: tm.price_ht ?? initial?.eur ?? offer?.eur ?? "",
           basis: tm.basis || "period", currency: tm.currency || "eur", free_months: tm.free_months ?? 0, discount_pct: tm.discount_pct ?? 0,
           discount_months: tm.discount_months ?? 0, apps: offer?.apps ?? initial?.apps ?? "", validity_days: 7,
           employees_paid: tm.employees_paid ?? 0, employees_offered: tm.employees_offered ?? 0,
           employees_pool: tm.employees_pool ?? 0, employees_pool_offered: tm.employees_pool_offered ?? 0 };
};

// Same form for developers (app limit) and mini-site clients (no limit): live summary before validation.
export const OfferForm = ({ d, onChanged, initial, conversationId, scope = null }) => {
  const { t } = useI18n();
  const mini = d.kind === "minisite";
  const offer = d.custom_offer && !d.custom_offer.archived ? d.custom_offer : null;
  const [f, setF] = useState(() => initialTerms(offer, initial));
  const [group, setGroup] = useState(() => ({ on: !!offer?.group, members: offer?.group?.members || [d.email], activation_email: offer?.group?.activation_email ?? true,
    refsText: Object.entries(offer?.group?.member_refs || {}).map(([e, r]) => `${e}: ${r.join(", ")}`).join("\n") }));
  const memberRefs = Object.fromEntries(group.refsText.split("\n").map((l) => l.split(":")).filter((x) => x.length >= 2 && x[0].includes("@"))
    .map(([e, ...r]) => [e.trim().toLowerCase(), r.join(":").split(/[,;\s]+/).map((x) => x.trim().toUpperCase()).filter(Boolean)]));
  const members = uniqueMembers(group.members);
  const sameScope = offer?.business && scope && offer.business.app === scope.app && offer.business.email === scope.email;
  const [refs, setRefs] = useState(() => (sameScope ? offer.business.refs : scope ? [scope.ref] : []));
  const biz = mini && !!scope;
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const money = useOfferMoney(f.currency);
  const set = (k, cast = num) => (e) => setF((x) => ({ ...x, [k]: e.target.value === "" ? "" : cast(e.target.value) }));
  const body = { ...f, flex: true, interval: f.mode === "prepaid" ? 12 : f.interval, apps: mini ? null : f.apps || null, conversation_id: conversationId,
    group: mini && group.on && !biz ? { members, activation_email: group.activation_email, member_refs: memberRefs } : null,
    employees_paid: mini ? num(f.employees_paid) || 0 : 0, employees_offered: mini ? num(f.employees_offered) || 0 : 0,
    employees_pool: biz ? num(f.employees_pool) || 0 : 0, employees_pool_offered: biz ? num(f.employees_pool_offered) || 0 : 0,
    business: biz ? { refs } : null,
    businesses: biz ? Math.max(1, refs.length) : mini && group.on ? Math.max(1, members.length) : 1 };
  const pool = splitPool((body.employees_pool || 0) + (body.employees_pool_offered || 0), refs);
  const key = JSON.stringify(body);
  useEffect(() => {
    if (!(num(f.price_ht) > 0 && num(f.months) >= 1)) return setQ(null);
    const id = setTimeout(() => api.post(`/admin/developers/${d.id}/custom-plan/quote`, body).then(({ data }) => setQ(data)).catch(() => setQ(null)), 300);
    return () => clearTimeout(id);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(t(mini && msg === "custom.saved" ? "custom.savedMini" : msg));
      if (data.notified === false) toast.warning(t("msg.notNotified"));
      onChanged();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const submit = (e) => {
    e.preventDefault();
    run(() => api.put(`/admin/developers/${d.id}/custom-plan`, body), "custom.saved");
  };
  return (
    <form onSubmit={submit} className="mt-3 min-w-0 space-y-3 rounded-xl border border-[rgba(245,197,66,0.2)] p-3" data-testid={`custom-plan-form-${d.id}`}>
      <p className="flex items-center gap-2 text-sm font-semibold text-white"><Sparkles className="h-4 w-4 text-[var(--gold)]" /> {t(mini ? "offer.titleMini" : "custom.title")}</p>
      <div className="flex flex-wrap gap-1.5" data-testid={`offer-presets-${d.id}`}>
        {PRESETS.map((m) => (
          <button key={m} type="button" onClick={() => setF((x) => ({ ...x, months: m }))} aria-pressed={f.months === m}
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${f.months === m ? "border-[var(--gold)] bg-[var(--gold)] text-black" : "border-neutral-600 text-neutral-200"}`} data-testid={`offer-preset-${m}`}>
            {t("offer.monthsN", { n: m })}
          </button>
        ))}
      </div>
      <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("offer.f.months")}><Input inputMode="numeric" value={f.months} onChange={set("months", parseInt)} className="mt-1 bg-black/50" data-testid="offer-months" /></Field>
        <Field label={t("offer.f.mode")}>
          <select value={f.mode} onChange={set("mode", String)} className={SEL} data-testid="offer-mode">
            <option value="recurring">{t("offer.f.recurring")}</option><option value="prepaid">{t("offer.f.prepaid")}</option>
          </select>
        </Field>
        {f.mode === "recurring" && <Field label={t("offer.f.interval")}><Input inputMode="numeric" value={f.interval} onChange={set("interval", parseInt)} className="mt-1 bg-black/50" data-testid="offer-interval" /></Field>}
        <Field label={t("offer.f.currency")}>
          <select value={f.currency} onChange={set("currency", String)} className={SEL} data-testid="offer-currency"><option value="eur">EUR €</option><option value="usd">USD $</option></select>
        </Field>
        <Field label={t(f.mode === "prepaid" ? "offer.f.priceMonth" : "offer.f.pricePeriod")}>
          <Input inputMode="decimal" value={f.price_ht} onChange={set("price_ht")} className="mt-1 bg-black/50" data-testid="offer-price" />
        </Field>
        <Field label={t("offer.f.basis")}>
          <select value={f.basis} onChange={set("basis", String)} className={SEL} data-testid="offer-basis">
            <option value="period">{t(f.mode === "prepaid" ? "offer.f.perMonth" : "offer.f.perPeriod")}</option><option value="total">{t("offer.f.total")}</option>
          </select>
        </Field>
        <Field label={t("offer.f.free")}><Input inputMode="numeric" value={f.free_months} onChange={set("free_months", parseInt)} className="mt-1 bg-black/50" data-testid="offer-free" /></Field>
        <Field label={t("offer.f.discount")}><Input inputMode="decimal" value={f.discount_pct} onChange={set("discount_pct")} className="mt-1 bg-black/50" data-testid="offer-discount" /></Field>
        {mini && <Field label={t("emp.fPaid")}><Input inputMode="numeric" value={f.employees_paid} onChange={set("employees_paid", parseInt)} className="mt-1 bg-black/50" data-testid="offer-employees-paid" /></Field>}
        {mini && <Field label={t("emp.fOffered")}><Input inputMode="numeric" value={f.employees_offered} onChange={set("employees_offered", parseInt)} className="mt-1 bg-black/50" data-testid="offer-employees-offered" /></Field>}
        <Field label={t("offer.f.discountMonths")}><Input inputMode="numeric" value={f.discount_months} onChange={set("discount_months", parseInt)} className="mt-1 bg-black/50" data-testid="offer-discount-months" /></Field>
        {!mini && <Field label={t("custom.apps")}><Input inputMode="numeric" value={f.apps} onChange={set("apps", parseInt)} className="mt-1 bg-black/50" data-testid={`custom-plan-apps-${d.id}`} /></Field>}
        <Field label={t("offer.f.validity")}><Input inputMode="numeric" value={f.validity_days} onChange={set("validity_days", parseInt)} className="mt-1 bg-black/50" data-testid="offer-validity" /></Field>
      </div>
      {biz && (
        <>
          <BusinessRefsField scope={scope} refs={refs} setRefs={setRefs} />
          <div className="grid min-w-0 gap-2 sm:grid-cols-2">
            <Field label={t("biz.poolPaid")}><Input inputMode="numeric" value={f.employees_pool} onChange={set("employees_pool", parseInt)} className="mt-1 bg-black/50" data-testid="offer-employees-pool" /></Field>
            <Field label={t("biz.poolOffered")}><Input inputMode="numeric" value={f.employees_pool_offered} onChange={set("employees_pool_offered", parseInt)} className="mt-1 bg-black/50" data-testid="offer-employees-pool-offered" /></Field>
          </div>
          {refs.length > 0 && pool.some(Boolean) && (
            <p className={`break-words text-[0.7rem] ${pool.some((n) => n + body.employees_paid + body.employees_offered > 50) ? "text-red-300" : "text-neutral-400"}`} data-testid="offer-employees-split">
              {t("biz.split", { split: refs.slice(0, 20).map((r, i) => `${r}: ${pool[i] + body.employees_paid + body.employees_offered}`).join(" · ") + (refs.length > 20 ? " …" : "") })}
            </p>
          )}
        </>
      )}
      {mini && !biz && (
        <div className="min-w-0 space-y-2 rounded-lg border border-emerald-500/30 p-2.5" data-testid={`offer-group-${d.id}`}>
          <label className="flex min-w-0 items-center justify-between gap-3 text-sm font-semibold text-white">
            <span className="min-w-0 break-words">{t("group.toggle")}</span>
            <Switch checked={group.on} onCheckedChange={(on) => setGroup((g) => ({ ...g, on }))} data-testid="offer-group-toggle" />
          </label>
          {group.on && (
            <>
              <p className="break-words text-[0.7rem] text-neutral-400">{t("group.hint", { payer: d.email })}</p>
              <GroupMembersField members={group.members} onChange={(m) => setGroup((g) => ({ ...g, members: m }))} testId="offer-group-members" />
              <label className="block min-w-0 text-xs text-neutral-300"><span className="break-words">{t("group.memberRefs")}</span>
                <textarea rows={3} value={group.refsText} onChange={(e) => setGroup((g) => ({ ...g, refsText: e.target.value }))} placeholder="email@exemple.fr: STP-AAAA-0002, STP-AAAA-0003"
                  className="mt-1 w-full min-w-0 rounded-md border border-neutral-700 bg-black/50 px-2 py-1.5 text-xs text-white" data-testid="offer-group-member-refs" /></label>
              <label className="flex min-w-0 items-center justify-between gap-3 text-xs text-neutral-300">
                <span className="min-w-0 break-words">{t("group.activationEmail")}</span>
                <Switch checked={group.activation_email} onCheckedChange={(v) => setGroup((g) => ({ ...g, activation_email: v }))} data-testid="offer-group-activation" />
              </label>
              {q && members.length > 0 && <p className="break-words text-sm font-bold text-emerald-200" data-testid="offer-group-summary">{t("group.summary", { n: members.length, price: money(q.total_ttc) })}</p>}
            </>
          )}
        </div>
      )}
      {q && <OfferProfit d={d} q={q} businesses={body.businesses} />}
      {q && (
        <div className="min-w-0" data-testid={`offer-summary-${d.id}`}>
          <OfferCard a={{ terms: body, quote: q, kind: d.kind, apps: body.apps, group: body.group && { members, size: members.length }, business: body.business && { app: scope.app, refs, employees: Object.fromEntries(refs.map((r, i) => [r, pool[i] + body.employees_paid + body.employees_offered])) }, state: "preview", created_at: new Date().toISOString() }} admin testId={`offer-preview-${d.id}`} />
          <p className="mt-2 break-words text-xs font-semibold text-emerald-200" data-testid={`offer-net-${d.id}`}>{t("offer.netEst", { net: money(q.net_est), fees: money(q.fees_est) })}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="btn-gold btn-sm" disabled={busy || !q || (mini && group.on && !biz && !members.length) || (biz && !refs.length)} data-testid={`custom-plan-submit-${d.id}`}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t(offer ? "custom.update" : "custom.create")}
        </button>
        {offer && (
          <ConfirmButton title={t("custom.archive")} description={t("custom.archive")} className="btn-red btn-sm"
            onConfirm={() => run(() => api.delete(`/admin/developers/${d.id}/custom-plan`), "custom.archived")} testId={`custom-plan-archive-${d.id}`}>
            <Archive className="h-4 w-4" /> {t("custom.archive")}
          </ConfirmButton>
        )}
      </div>
    </form>
  );
};
